import{selectSignatureCue,type HeroCombatId,type SignatureAttackKind}from'./heroCombatIdentity'
import type{AerialMode}from'./aerialMotion'
import type{AttackPhase}from'./simulation'

export type HeroMovementMode='ground'|'air'|'flight'|'wall'|'underwater'
export type HeroTargetScale='small'|'human'|'large'|'colossus'|'boss'
export type HeroPowerState='normal'|'charged'|'overdrive'|'finisher'
export type HeroPerformanceContext={
 heroId:HeroCombatId;attack:SignatureAttackKind;comboCount:number
 movementMode:HeroMovementMode;attackPhase:Exclude<AttackPhase,null>
 targetScale?:HeroTargetScale;finisherEligible?:boolean
}
export type HeroPerformancePacket={
 heroId:HeroCombatId;attack:SignatureAttackKind;skillName:string;animationCue:string
 variantId:string;movementMode:HeroMovementMode;motionCue:string;powerState:HeroPowerState
 vfxLayers:readonly string[];sfxLayers:readonly string[]
 camera:{preset:string;impulse:number;fovPulse:number}
 haptics:{low:number;high:number;durationMs:number}
 impact:string;intensity:number;targetScale:HeroTargetScale
}
export type HeroPerformanceCameraCue={
 preset:string;side:'player'|'enemy';impulse:number;fovPulse:number;duration:number;ttl:number
}

const clamp=(v:number,min:number,max:number)=>Math.max(min,Math.min(max,Number.isFinite(v)?v:min))
const unique=(values:string[])=>[...new Set(values.filter(Boolean))]
export function movementModeForAerial(mode:AerialMode):HeroMovementMode{
 if(mode==='flight')return'flight'
 if(mode==='ground')return'ground'
 return'air'
}
export function targetScaleForHero(heroId:string):HeroTargetScale{
 if(heroId==='tita-montanhas')return'colossus'
 if(heroId==='golem-vivo'||heroId==='dragao-cristal'||heroId==='fenix-primordial'||heroId==='alien-commander')return'large'
 return'human'
}
const motionCue=(mode:HeroMovementMode,role:string)=>mode==='flight'?'flight-lunge':mode==='air'?'air-chain':mode==='wall'?'wall-rebound':mode==='underwater'?'hydro-vector':role==='mobility'?'ground-burst':'ground-commit'
const powerState=(attack:SignatureAttackKind,comboCount:number,finisher:boolean):HeroPowerState=>finisher?'finisher':attack==='special'?'overdrive':comboCount>=3?'charged':'normal'
const cameraPreset=(mode:HeroMovementMode,state:HeroPowerState)=>state==='finisher'?'finisher-focus':mode==='flight'||mode==='air'?'aerial-track':mode==='wall'?'wall-impact':state==='overdrive'?'overdrive-push':'combat-punch'

export function deriveHeroPerformancePacket(context:HeroPerformanceContext):HeroPerformancePacket{
 if(!Number.isSafeInteger(context.comboCount)||context.comboCount<0)throw new Error(`Invalid performance combo count: ${context.comboCount}`)
 const cue=selectSignatureCue(context.heroId,context.attack,context.comboCount)
 const mode=context.movementMode,targetScale=context.targetScale??'human'
 const state=powerState(context.attack,context.comboCount,Boolean(context.finisherEligible))
 const comboTier=Math.min(4,context.comboCount)
 const phaseGain=context.attackPhase==='active'?1:context.attackPhase==='startup'?.72:.5
 const stateGain=state==='finisher'?1.55:state==='overdrive'?1.32:state==='charged'?1.16:1
 const scaleGain=targetScale==='colossus'||targetScale==='boss'?1.18:targetScale==='large'?1.08:1
 const intensity=clamp((.72+cue.motion.cameraImpulse*1.9+comboTier*.08)*phaseGain*stateGain*scaleGain,.35,2.4)
 const impulse=clamp(cue.motion.cameraImpulse*(state==='finisher'?1.55:state==='overdrive'?1.28:1)*(mode==='air'||mode==='flight'?1.08:1),0,.85)
 const low=clamp(.22+cue.motion.hitstop*2.7+(cue.impact==='crush'?.25:0),0,1)
 const high=clamp(.28+cue.motion.tempo*.32+(cue.trail==='rift'||cue.trail==='prism'?.18:0),0,1)
 const motion=motionCue(mode,cue.role),variantId=[cue.animationVariant,mode,state,targetScale].join(':')
 return{
  heroId:context.heroId,attack:context.attack,skillName:cue.skillName,animationCue:cue.animationVariant,
  variantId,movementMode:mode,motionCue:motion,powerState:state,
  vfxLayers:unique([
   `trail:${cue.trail}`,`impact:${cue.impact}`,`motion:${motion}`,
   state!=='normal'?`power:${state}`:'',
   comboTier>=2?`combo:tier-${comboTier}`:'',
   targetScale==='colossus'||targetScale==='boss'?`scale:${targetScale}`:'',
  ]),
  sfxLayers:unique([
   `skill:${context.heroId}:${context.attack}`,`trail:${cue.trail}`,`impact:${cue.impact}`,
   state!=='normal'?`state:${state}`:'',
  ]),
  camera:{preset:cameraPreset(mode,state),impulse,fovPulse:clamp(impulse*.72,0,.55)},
  haptics:{low,high,durationMs:Math.round(clamp(55+cue.motion.hitstop*680+comboTier*12,45,190))},
  impact:cue.impact,intensity,targetScale,
 }
}

export function heroPerformanceAudioPlan(packet:HeroPerformancePacket,side:'player'|'enemy'){
 const stateBase=packet.powerState==='finisher'?82:packet.powerState==='overdrive'?108:packet.powerState==='charged'?154:212
 const modeScale=packet.movementMode==='flight'?1.3:packet.movementMode==='air'?1.16:packet.movementMode==='wall'?.84:packet.movementMode==='underwater'?.68:1
 const frequency=clamp(stateBase*modeScale,44,520)
 const volume=clamp(.014+packet.intensity*.018,.012,.075)
 const duration=clamp(.065+packet.intensity*.052,.055,.24)
 const type:OscillatorType=packet.powerState==='finisher'?'sawtooth':packet.powerState==='overdrive'?'triangle':'sine'
 return{frequency,volume,duration,pan:side==='player'?-.28:.28,type,harmonic:packet.powerState==='normal'?null:Math.min(920,frequency*(packet.powerState==='finisher'?2.5:2))}
}
export function heroPerformanceRumblePlan(packet:HeroPerformancePacket,side:'player'|'enemy',localMode:boolean){
 const enemyTelegraph=!localMode&&side==='enemy',scale=enemyTelegraph?.34:1
 return{
  padIndex:localMode&&side==='enemy'?1:0,
  strong:clamp(packet.haptics.low*scale,0,1),
  weak:clamp(packet.haptics.high*scale,0,1),
  duration:Math.round(clamp(packet.haptics.durationMs*(enemyTelegraph?.72:1),35,220)),
 }
}
export function createHeroPerformanceCameraCue(packet:HeroPerformancePacket,side:'player'|'enemy'):HeroPerformanceCameraCue{
 const duration=packet.powerState==='finisher'?.72:packet.powerState==='overdrive'?.48:packet.powerState==='charged'?.34:.24
 return{preset:packet.camera.preset,side,impulse:clamp(packet.camera.impulse,0,.85),fovPulse:clamp(packet.camera.fovPulse,0,.55),duration,ttl:duration}
}
export function stepHeroPerformanceCameraCue(cue:HeroPerformanceCameraCue|null,dt:number){
 if(!cue)return{cue:null,gain:0,impulse:0,fovPulse:0}
 const ttl=Math.max(0,cue.ttl-Math.max(0,Number.isFinite(dt)?dt:0))
 const gain=cue.duration>0?clamp(ttl/cue.duration,0,1):0
 return{cue:ttl>0?{...cue,ttl}:null,gain,impulse:cue.impulse*gain,fovPulse:cue.fovPulse*gain}
}
